import { Injectable, inject } from '@angular/core';
import { Localization } from '@freelancer/localization';
import type { Locale } from 'date-fns';
import {
  format,
  formatDistance,
  formatDistanceStrict,
  formatDuration,
  setDay,
} from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import type { Observable } from 'rxjs';
import { from } from 'rxjs';

/** date-fns doesn't tell us what this is, so use the ISO standard of Monday. */
export const START_OF_WEEK = 1;

interface StrictOptions {
  readonly addSuffix?: boolean;
  readonly unit?: 'second' | 'minute' | 'hour' | 'day' | 'month' | 'year';
  readonly roundingMethod?: 'floor' | 'ceil' | 'round';
  readonly locale?: Locale;
  readonly shortFormat?: boolean;
  readonly includeSpace?: boolean;
}

interface Options {
  includeSeconds?: boolean;
  addSuffix?: boolean;
  locale?: Locale;
  readonly shortFormat?: boolean;
  readonly includeSpace?: boolean;
}

interface DurationOptions {
  readonly shortFormat?: boolean;
  readonly locale?: Locale;
  readonly includeSpace?: boolean;
}

// `fil`, `sq`, and `sw` have no date-fns package (T267853).
const DATE_FNS_LOCALE_LOADERS: {
  readonly [tag: string]: () => Promise<Locale>;
} = {
  af: () => import('date-fns/locale/af').then(module => module.default),
  bn: () => import('date-fns/locale/bn').then(module => module.default),
  ca: () => import('date-fns/locale/ca').then(module => module.default),
  cs: () => import('date-fns/locale/cs').then(module => module.default),
  da: () => import('date-fns/locale/da').then(module => module.default),
  de: () => import('date-fns/locale/de').then(module => module.default),
  el: () => import('date-fns/locale/el').then(module => module.default),
  'en-AU': () => import('date-fns/locale/en-AU').then(module => module.default),
  'en-CA': () => import('date-fns/locale/en-CA').then(module => module.default),
  'en-GB': () => import('date-fns/locale/en-GB').then(module => module.default),
  'en-IE': () => import('date-fns/locale/en-IE').then(module => module.default),
  'en-IN': () => import('date-fns/locale/en-IN').then(module => module.default),
  'en-NZ': () => import('date-fns/locale/en-NZ').then(module => module.default),
  'en-US': () => import('date-fns/locale/en-US').then(module => module.default),
  'en-ZA': () => import('date-fns/locale/en-ZA').then(module => module.default),
  es: () => import('date-fns/locale/es').then(module => module.default),
  fi: () => import('date-fns/locale/fi').then(module => module.default),
  fr: () => import('date-fns/locale/fr').then(module => module.default),
  hi: () => import('date-fns/locale/hi').then(module => module.default),
  hu: () => import('date-fns/locale/hu').then(module => module.default),
  id: () => import('date-fns/locale/id').then(module => module.default),
  it: () => import('date-fns/locale/it').then(module => module.default),
  ja: () => import('date-fns/locale/ja').then(module => module.default),
  ko: () => import('date-fns/locale/ko').then(module => module.default),
  ms: () => import('date-fns/locale/ms').then(module => module.default),
  nb: () => import('date-fns/locale/nb').then(module => module.default),
  nl: () => import('date-fns/locale/nl').then(module => module.default),
  pl: () => import('date-fns/locale/pl').then(module => module.default),
  pt: () => import('date-fns/locale/pt').then(module => module.default),
  ro: () => import('date-fns/locale/ro').then(module => module.default),
  sl: () => import('date-fns/locale/sl').then(module => module.default),
  sv: () => import('date-fns/locale/sv').then(module => module.default),
  th: () => import('date-fns/locale/th').then(module => module.default),
  tr: () => import('date-fns/locale/tr').then(module => module.default),
  uk: () => import('date-fns/locale/en-GB').then(module => module.default),
  vi: () => import('date-fns/locale/vi').then(module => module.default),
  zh: () => import('date-fns/locale/zh-CN').then(module => module.default),
};

function dateFnsLocaleLoader(
  tag: string | undefined,
): (() => Promise<Locale>) | undefined {
  if (!tag) {
    return undefined;
  }

  // Loadshift's build locale has no date-fns package.
  return DATE_FNS_LOCALE_LOADERS[tag === 'en-CUSTOM' ? 'en-AU' : tag];
}

@Injectable()
export class LocalizedDateFns {
  private localization = inject(Localization);

  localePromise: Promise<Locale>;
  /** Set once `localePromise` resolves, for the synchronous formatters below. */
  private loadedLocale?: Locale;

  constructor() {
    const navigatorLanguage =
      typeof navigator !== 'undefined' ? navigator.language : undefined;
    // Site region wins (`en-AU` on freelancer.com.au). Bare `en` has no
    // date-fns locale, so that case still follows the browser language.
    const loadLocale =
      dateFnsLocaleLoader(this.localization.locale) ??
      dateFnsLocaleLoader(this.localization.languageCode) ??
      dateFnsLocaleLoader(navigatorLanguage);

    if (loadLocale) {
      this.localePromise = loadLocale();
    } else {
      this.localePromise = import('date-fns/locale/en-US').then(
        module => module.default,
      );
      // `en` is the bare-English fallback, not a missing locale. SSR has no
      // browser language, and logging here fires on every freelancer.com render.
      if (this.localization.languageCode !== 'en') {
        console.error(
          `Locale ${this.localization.languageCode} isn't configured for date-fns, not all dates will be localized`,
        );
      }
    }

    this.localePromise = this.localePromise.catch(async error => {
      console.error(
        `Failed to load date-fns locale for ${this.localization.languageCode}, falling back to en-US: ${error}`,
      );
      return (await import('date-fns/locale/en-US')).default;
    });

    this.localePromise.then(loadedLocale => {
      this.loadedLocale = loadedLocale;
    });
  }

  /**
   * Locale-aware `date-fns` `format`. Not localised until `localePromise`
   * resolves, as that downloads the locale chunk.
   */
  format(date: Date, formatStr: string): string {
    try {
      return format(date, formatStr, { locale: this.loadedLocale });
    } catch {
      return 'Invalid Date';
    }
  }

  /** Day names starting on Sunday, as `date-fns` locales order them. */
  getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): readonly string[] {
    const formatStr = {
      long: 'EEEE', // 'Monday'
      short: 'EEE', // 'Mon'
      narrow: 'EEEEEE', // 'Mo'
    }[style];
    const date = new Date(2019, 0, 1);

    return Array.from({ length: 7 }, (_unused, day) =>
      this.format(
        setDay(date, day, { weekStartsOn: START_OF_WEEK }),
        formatStr,
      ),
    );
  }

  /**
   * Returns the format used to display numeric longform (day + month + year) dates.
   * Changes based on the locale.
   */
  get dateFormat(): Promise<string> {
    return this.localePromise.then(locale =>
      locale.formatLong
        ? locale.formatLong.date({ width: 'short' })
        : 'MM/dd/yyyy',
    );
  }

  /**
   * Returns the format used to display numeric longform (day + month + year) date ranges
   * Changes based on the locale.
   */
  get dateRangeFormat(): Promise<string> {
    return this.localePromise.then(locale => {
      const single = locale.formatLong
        ? locale.formatLong.date({ width: 'short' })
        : 'MM/dd/yyyy';
      return `${single} - ${single}`;
    });
  }

  /**
   * Returns a list of localised months, if no locale is set English months are
   * returned.
   */
  getLocalisedMonths(): Observable<readonly string[]> {
    return from(
      this.localePromise.then(locale =>
        locale.localize
          ? [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11].map(month =>
              locale.localize?.month(month),
            )
          : [
              'January',
              'February',
              'March',
              'April',
              'May',
              'June',
              'July',
              'August',
              'September',
              'October',
              'November',
              'December',
            ],
      ),
    );
  }

  formatDistance(
    date: Date | number,
    baseDate: Date | number,
    options?: Options,
  ): Promise<string> {
    return this.localePromise.then(locale =>
      formatDistance(date, baseDate, {
        ...options,
        locale: {
          ...locale,
          ...(options?.shortFormat
            ? {
                formatDistance: (token: string, count: number) => {
                  // Keys from `FormatDistanceFn` token field in https://github.com/date-fns/date-fns/blob/v2.19.0/src/locale/types.ts
                  const shortFormat: Record<string, (count: number) => string> =
                    {
                      lessThanXSeconds: c =>
                        $localize`:Time in seconds in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}s`,
                      xSeconds: c =>
                        $localize`:Time in seconds in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}s`,
                      halfAMinute: () =>
                        $localize`:Time in seconds in abbreviated format:30${options?.includeSpace ? ' ' : ''}s`,
                      lessThanXMinutes: c =>
                        $localize`:Time in minutes in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}m`,
                      xMinutes: c =>
                        $localize`:Time in minutes in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}m`,
                      aboutXHours: c =>
                        $localize`:Time in hours in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}h`,
                      xHours: c =>
                        $localize`:Time in hours in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}h`,
                      xDays: c =>
                        $localize`:Time in days in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}d`,
                      aboutXMonths: c =>
                        $localize`:Time in months in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}mo`,
                      xMonths: c =>
                        $localize`:Time in months in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}mo`,
                      aboutXYears: c =>
                        $localize`:Time in years in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}y`,
                      xYears: c =>
                        $localize`:Time in years in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}y`,
                      overXYears: c =>
                        $localize`:Time in years in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}y`,
                      almostXYears: c =>
                        $localize`:Time in years in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}y`,
                    };
                  return shortFormat[token](count);
                },
              }
            : {}),
        },
      }),
    );
  }

  formatDistanceStrict(
    date: Date | number,
    baseDate: Date | number,
    options?: StrictOptions,
  ): Promise<string> {
    return this.localePromise.then(locale =>
      formatDistanceStrict(date, baseDate, {
        ...options,
        locale: {
          ...locale,
          ...(options?.shortFormat
            ? {
                formatDistance: (token: string, count: number) => {
                  // Keys from `FormatDistanceFn` token field in https://github.com/date-fns/date-fns/blob/v2.19.0/src/locale/types.ts
                  const shortFormat: Record<string, (count: number) => string> =
                    {
                      lessThanXSeconds: c =>
                        $localize`:Time in seconds in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}s`,
                      xSeconds: c =>
                        $localize`:Time in seconds in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}s`,
                      halfAMinute: () =>
                        $localize`:Time in seconds in abbreviated format:30${options?.includeSpace ? ' ' : ''}s`,
                      lessThanXMinutes: c =>
                        $localize`:Time in minutes in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}m`,
                      xMinutes: c =>
                        $localize`:Time in minutes in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}m`,
                      aboutXHours: c =>
                        $localize`:Time in hours in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}h`,
                      xHours: c =>
                        $localize`:Time in hours in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}h`,
                      xDays: c =>
                        $localize`:Time in days in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}d`,
                      aboutXMonths: c =>
                        $localize`:Time in months in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}mo`,
                      xMonths: c =>
                        $localize`:Time in months in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}mo`,
                      aboutXYears: c =>
                        $localize`:Time in years in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}y`,
                      xYears: c =>
                        $localize`:Time in years in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}y`,
                      overXYears: c =>
                        $localize`:Time in years in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}y`,
                      almostXYears: c =>
                        $localize`:Time in years in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}y`,
                    };
                  return shortFormat[token](count);
                },
              }
            : {}),
        },
      }),
    );
  }

  formatDuration(
    duration: Duration,
    options?: DurationOptions,
  ): Promise<string> {
    return this.localePromise.then(locale =>
      formatDuration(duration, {
        ...options,
        locale: {
          ...locale,
          ...(options?.shortFormat
            ? {
                formatDistance: (token: string, count: number) => {
                  // Keys from `FormatDistanceFn` token field for duration formatting
                  const shortFormat: Record<string, (count: number) => string> =
                    {
                      lessThanXSeconds: c =>
                        $localize`:Time in seconds in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}s`,
                      xSeconds: c =>
                        $localize`:Time in seconds in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}s`,
                      halfAMinute: () =>
                        $localize`:Time in seconds in abbreviated format:30${options?.includeSpace ? ' ' : ''}s`,
                      lessThanXMinutes: c =>
                        $localize`:Time in minutes in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}m`,
                      xMinutes: c =>
                        $localize`:Time in minutes in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}m`,
                      aboutXHours: c =>
                        $localize`:Time in hours in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}h`,
                      xHours: c =>
                        $localize`:Time in hours in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}h`,
                      xDays: c =>
                        $localize`:Time in days in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}d`,
                      aboutXMonths: c =>
                        $localize`:Time in months in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}mo`,
                      xMonths: c =>
                        $localize`:Time in months in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}mo`,
                      aboutXYears: c =>
                        $localize`:Time in years in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}y`,
                      xYears: c =>
                        $localize`:Time in years in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}y`,
                      overXYears: c =>
                        $localize`:Time in years in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}y`,
                      almostXYears: c =>
                        $localize`:Time in years in abbreviated format:${c}${options?.includeSpace ? ' ' : ''}y`,
                    };
                  return shortFormat[token](count);
                },
              }
            : {}),
        },
      }),
    );
  }

  /**
   * Converts a Unix timestamp to a formatted date-time string in the specified timezone.
   *
   * @param timestamp - The Unix timestamp in milliseconds.
   * @param timezone - The IANA timezone string (e.g., "Australia/Sydney").
   * @param dateFormat - A date-fns format string (e.g., "yyyy-MM-dd'T'HH:mm")
   *
   * @returns A string formatted as "yyyy-MM-ddT'HH:mm".
   */
  formatTimestampToZonedString(
    timestamp: number,
    timezone: string,
    dateFormat = "yyyy-MM-dd'T'HH:mm",
  ): string {
    return format(utcToZonedTime(new Date(timestamp), timezone), dateFormat);
  }

  /**
   * Converts a formatted date-time string in a specific timezone to a Unix timestamp.
   *
   * @param datetime - A string representing the date and time (e.g., "2025-08-04T14:30").
   * @param timezone - The IANA timezone string (e.g., "Australia/Sydney").
   *
   * @returns The Unix timestamp in milliseconds (UTC).
   */
  parseZonedStringToTimestamp(datetime: string, timezone: string): number {
    return zonedTimeToUtc(new Date(datetime), timezone).getTime();
  }
}
